/**
 * Searchable Select Component
 *
 * A single-select combobox with typeahead filtering, composed from the design
 * system's `Popover` + `Command` primitives.
 *
 * NOT a duplicate of the design system's `Select`: that one has no search
 * field. Same reasoning (and the same long-term home) as [MultiSelect].
 */

import { useState } from 'react';
import { Check, ChevronsUpDown } from '@lognai/synapse-design-system-v2/icons';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
} from '@lognai/synapse-design-system-v2';

export interface SearchableSelectOption {
  value: string;
  label: string;
  description?: string;
  /**
   * Shown but not choosable. For an option that EXISTS but cannot be used yet —
   * leaving it out would read as "it isn't there", which is a different and far
   * more confusing answer than "it is there, and here is why you can't pick it".
   */
  disabled?: boolean;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  disabled = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          {/* CommandList is cmdk's scroll container (the previous overflow-auto
              on CommandGroup did not scroll). Mirrors PortSearchCombobox. */}
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  // cmdk filters on this string — include label + description so
                  // searching by name, code, or category all match.
                  value={`${option.label} ${option.description ?? ''}`}
                  disabled={option.disabled}
                  onSelect={() => {
                    if (option.disabled) return;
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value === option.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{option.label}</div>
                    {option.description && (
                      <div className="text-xs text-muted-foreground">{option.description}</div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
